import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  FormControlLabel,
  Switch,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import apiClient from '../utils/axios';
import { adjustPriceToTickSize, getTickSize } from '../utils/priceUtils';
import { isNXTTradingHours, isKRXSessionOpen, isWeekend, isHolidaySync } from '../utils/tradingHours';

const BuyStockDialog = ({ open, onClose, stock, onOpenRepeatAutoTrading }) => {
  const [quantity, setQuantity] = useState('');
  const [orderType, setOrderType] = useState('market'); // 시장가를 기본값으로
  const [price, setPrice] = useState('');
  const [targetPrice, setTargetPrice] = useState(''); // 목표가격
  const [targetPricePercent, setTargetPricePercent] = useState(''); // 목표가격 % (현재가 기준)
  const [autoBuy, setAutoBuy] = useState(true); // 자동매수 여부 (기본값: 활성화)
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(0);

  // 현재가 조회 (장중 REST 생략 — 부모 시세/WS 사용)
  const fetchCurrentPrice = useCallback(async () => {
    if (!stock?.stockCode) return;

    if (stock.price && Number(stock.price) > 0) {
      setCurrentPrice(Number(stock.price));
    }

    const inMarket =
      !isWeekend() &&
      !isHolidaySync() &&
      (isKRXSessionOpen() || isNXTTradingHours());
    if (inMarket) return;

    try {
      const response = await apiClient.get('/watchlist/prices');
      const priceInfo = response.data?.find(p => p.stockCode === stock.stockCode);
      if (priceInfo?.price) {
        setCurrentPrice(priceInfo.price);
      }
    } catch (error) {
      console.error('[매수] 현재가 조회 실패:', error);
    }
  }, [stock?.stockCode, stock?.price]);

  // 종목 정보가 변경되면 초기화
  useEffect(() => {
    if (open && stock) {
      setQuantity('');
      setOrderType('market');
      setPrice('');
      setTargetPrice('');
      setTargetPricePercent('');
      setAutoBuy(true);
      setErrorMessage(null);
      fetchCurrentPrice();
    }
  }, [open, stock, fetchCurrentPrice]);

  // 지정가 선택 시 현재가를 기본값으로 설정
  useEffect(() => {
    if (orderType === 'limit' && currentPrice > 0 && !price) {
      setPrice(String(currentPrice));
    } else if (orderType === 'market') {
      setPrice('');
    }
  }, [orderType, currentPrice, price]);

  // 매수주문 설정 저장
  const handleBuy = async () => {
    if (!stock) {
      setErrorMessage('종목 정보가 없습니다.');
      return;
    }

    if (!quantity || parseFloat(quantity) <= 0) {
      setErrorMessage('수량을 입력해주세요.');
      return;
    }

    if (orderType === 'limit' && (!price || parseFloat(price) <= 0)) {
      setErrorMessage('가격을 입력해주세요.');
      return;
    }

    // 목표가격에서 콤마 제거 후 검증
    const targetPriceNum = targetPrice ? parseFloat(targetPrice.replace(/,/g, '')) : 0;
    if (!targetPrice || targetPriceNum <= 0) {
      setErrorMessage('목표가격을 입력해주세요.');
      return;
    }

    try {
      setLoading(true);
      setErrorMessage(null);

      const orderTypeText = orderType === 'limit' ? '지정가' : '시장가';

      // target_buy에 저장 (목표가격은 콤마 제거한 숫자로 저장)
      await apiClient.post('/watchlist/target-buy', {
        stockCode: stock.stockCode,
        targetPrice: targetPriceNum,
        targetQty: parseInt(quantity),
        autoBuy: autoBuy ? 'Y' : 'N',
        orderType: orderTypeText,
      });

      alert(`매수주문 설정이 완료되었습니다.\n종목: ${stock.stockName} (${stock.stockCode})\n수량: ${quantity}주\n목표가격: ${targetPriceNum.toLocaleString()}원\n주문유형: ${orderTypeText}\n자동매수: ${autoBuy ? '활성화' : '비활성화'}`);
      
      onClose();
    } catch (error) {
      console.error('[매수주문 설정] 실패:', error);
      const message = error.response?.data?.error || '매수주문 설정 중 오류가 발생했습니다.';
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };


  // 가격 입력 검증
  const handlePriceChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setPrice(value);
    }
  };

  // 주문 유형 변경 시 가격 필드 처리
  const handleOrderTypeChange = (e) => {
    const newOrderType = e.target.value;
    setOrderType(newOrderType);
    if (newOrderType === 'limit' && currentPrice > 0 && !price) {
      setPrice(String(currentPrice));
    } else if (newOrderType === 'market') {
      setPrice('');
    }
  };

  // 목표가격 % 입력 시 목표가격 자동 계산 (호가 단위에 맞게 조정, 무조건 마이너스)
  const handleTargetPricePercentChange = (e) => {
    let value = e.target.value;
    // 숫자만 입력 가능, 마이너스는 자동으로 붙임
    if (value === '') {
      setTargetPricePercent('');
      setTargetPrice('');
      return;
    }
    
    // 숫자만 추출 (마이너스 제거)
    const numValue = value.replace(/[^0-9.]/g, '');
    if (numValue === '' || /^\d*\.?\d*$/.test(numValue)) {
      // 마이너스 자동 추가
      const percentValue = numValue ? `-${numValue}` : '';
      setTargetPricePercent(percentValue);
      
      if (numValue && currentPrice > 0) {
        const percent = parseFloat(`-${numValue}`);
        if (!isNaN(percent)) {
          const calculatedPrice = currentPrice * (1 + percent / 100);
          // 호가 단위에 맞게 조정 (버림)
          const adjustedPrice = adjustPriceToTickSize(calculatedPrice);
          setTargetPrice(String(adjustedPrice));
        }
      } else {
        setTargetPrice('');
      }
    }
  };

  // 목표가격 직접 입력 시 % 자동 계산 (호가 단위에 맞게 조정)
  const handleTargetPriceChange = (e) => {
    const value = e.target.value.replace(/,/g, ''); // 콤마 제거
    if (value === '' || /^\d+$/.test(value)) {
      // 호가 단위에 맞게 조정 (버림)
      const numValue = value ? adjustPriceToTickSize(parseInt(value)) : '';
      const formattedValue = numValue ? numValue.toLocaleString() : '';
      setTargetPrice(formattedValue);
      
      if (value && currentPrice > 0) {
        const target = parseInt(value);
        if (!isNaN(target)) {
          const percent = ((target / currentPrice - 1) * 100).toFixed(2);
          setTargetPricePercent(percent);
        }
      } else {
        setTargetPricePercent('');
      }
    }
  };

  if (!stock) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
      disableBackdropClick={loading}
      sx={{
        zIndex: 9999, // 최우선 창으로 설정
      }}
      PaperProps={{
        sx: {
          zIndex: 9999,
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">자동매수설정</Typography>
          <Button onClick={onClose} disabled={loading} size="small">
            ✕
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* 종목 정보 */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  종목코드
                </Typography>
                <Typography variant="h6">{stock.stockCode}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  종목명
                </Typography>
                <Typography variant="h6">{stock.stockName}</Typography>
              </Grid>
              {currentPrice > 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    현재가
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    {currentPrice.toLocaleString()}원
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}

          {/* 주문 유형 */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>주문 유형</InputLabel>
            <Select
              value={orderType}
              onChange={handleOrderTypeChange}
              label="주문 유형"
              disabled={loading}
            >
              <MenuItem value="market">시장가</MenuItem>
              <MenuItem value="limit">지정가</MenuItem>
            </Select>
          </FormControl>

          {/* 수량 입력 */}
          <TextField
            label="주문수량"
            type="number"
            fullWidth
            value={quantity}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                setQuantity(value);
              }
            }}
            placeholder="주문 수량을 입력하세요"
            sx={{ mb: 2 }}
            disabled={loading}
            helperText="주 단위로 입력하세요"
            inputProps={{
              min: 1,
              step: 1,
            }}
            InputProps={{
              endAdornment: <Typography variant="body2" sx={{ mr: 1 }}>주</Typography>
            }}
          />

          {/* 목표가격 % 입력과 목표가격 나란히 배치 (3:7 비율) */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={3}>
              <TextField
                label="목표가격 (%)"
                type="text"
                fullWidth
                value={targetPricePercent}
                onChange={handleTargetPricePercentChange}
                placeholder="예: 5"
                disabled={loading}
                helperText="% 입력"
                InputProps={{
                  startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>-</Typography>,
                  endAdornment: <Typography variant="body2" sx={{ mr: 1 }}>%</Typography>
                }}
              />
            </Grid>
            <Grid item xs={9}>
              <TextField
                label="목표가격"
                type="text"
                fullWidth
                value={targetPrice}
                onChange={handleTargetPriceChange}
                placeholder="목표 가격"
                disabled={loading}
                helperText={
                  targetPricePercent 
                    ? `현재가 대비 ${targetPricePercent}%` 
                    : currentPrice > 0 
                      ? `호가 단위: ${getTickSize(currentPrice)}원 (자동 조정됨)`
                      : '가격을 직접 입력하거나 %로 입력 (호가 단위에 맞게 자동 조정됩니다)'
                }
                InputProps={{
                  endAdornment: <Typography variant="body2" sx={{ mr: 1 }}>원</Typography>
                }}
              />
            </Grid>
          </Grid>

          {/* 가격 입력 (지정가일 때만) */}
          {orderType === 'limit' && (
            <TextField
              label="가격"
              type="text"
              fullWidth
              value={price}
              onChange={handlePriceChange}
              placeholder="주문 가격을 입력하세요"
              sx={{ mb: 2 }}
              disabled={loading}
              helperText={`현재가: ${currentPrice.toLocaleString()}원`}
              InputProps={{
                endAdornment: <Typography variant="body2" sx={{ mr: 1 }}>원</Typography>
              }}
            />
          )}

          {/* 자동매수 여부 */}
          <FormControlLabel
            control={
              <Switch
                checked={autoBuy}
                onChange={(e) => setAutoBuy(e.target.checked)}
                disabled={loading}
              />
            }
            label="자동매수 활성화"
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            자동매수 활성화 시 목표가격에 도달하면 자동으로 매수 주문이 실행됩니다.
          </Typography>

          {/* 예상 금액 (지정가일 때) */}
          {orderType === 'limit' && quantity && price && (
            <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                예상 주문 금액
              </Typography>
              <Typography variant="h6">
                {(parseInt(quantity) * parseFloat(price || 0)).toLocaleString()}원
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {onOpenRepeatAutoTrading && (
          <Button 
            onClick={() => {
              onClose();
              onOpenRepeatAutoTrading(stock);
            }}
            variant="outlined"
            color="primary"
            disabled={loading}
            startIcon={<SettingsIcon fontSize="small" />}
          >
            반복자동매매설정
          </Button>
        )}
        <Button onClick={onClose} disabled={loading}>
          취소
        </Button>
        <Button
          onClick={handleBuy}
          variant="contained"
          color="error"
          disabled={loading || !quantity || !targetPrice || (orderType === 'limit' && !price)}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          매수주문 설정
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BuyStockDialog;

